import React, { useState, useEffect, useRef } from 'react';
import { Send, X, BookOpen, CheckCircle, Lock, MessageCircle, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function StudyCompanion({ piece, moduleKey, isOpen, onClose, onComprehensionAchieved, token }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [comprehensionMet, setComprehensionMet] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'reflect'
  const [reflection, setReflection] = useState('');
  const [reflectionFeedback, setReflectionFeedback] = useState(null);
  const [submittingReflection, setSubmittingReflection] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && moduleKey) {
      loadHistory();
    }
  }, [isOpen, moduleKey]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/learning/companion/history/${moduleKey}`, {
        headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (e) { console.error('Load history error:', e); }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setSending(true);

    try {
      const res = await fetch(`${API_URL}/api/learning/companion/chat`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ message: userMsg, module_key: moduleKey }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
        if (data.comprehension_achieved) {
          setComprehensionMet(true);
          onComprehensionAchieved?.();
          toast.success('Your understanding of this passage has been confirmed!');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch (e) {
      console.error('Chat error:', e);
      toast.error('Connection error');
    }
    setSending(false);
  };

  const submitReflection = async () => {
    if (!reflection.trim() || submittingReflection) return;
    setSubmittingReflection(true);
    try {
      const res = await fetch(`${API_URL}/api/learning/progress/${moduleKey}/reflection`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ reflection: reflection.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setReflectionFeedback(data);
        if (data.comprehension_met) {
          setComprehensionMet(true);
          onComprehensionAchieved?.();
        }
      }
    } catch (e) { toast.error('Failed to submit'); }
    setSubmittingReflection(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg bg-[#0a0804] border border-amber-900/30 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-900/20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #92400e, #d97706)' }}>
              <BookOpen className="w-4 h-4 text-amber-100" />
            </div>
            <div>
              <p className="text-sm font-serif text-amber-300" data-testid="companion-title">Study Companion</p>
              <p className="text-[10px] text-amber-500/70 font-mono">{piece?.scripture_ref || moduleKey}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {comprehensionMet && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-900/30 text-emerald-400 text-[10px] font-mono" data-testid="comprehension-badge">
                <CheckCircle className="w-3 h-3" /> Comprehension Met
              </span>
            )}
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-amber-900/20 text-amber-600 transition-colors" data-testid="close-companion">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-amber-900/15">
          <button
            onClick={() => setView('chat')}
            className={`flex-1 py-2.5 text-xs font-mono tracking-wider transition-colors ${
              view === 'chat' ? 'text-amber-300 border-b-2 border-amber-500' : 'text-amber-600/50 hover:text-amber-500'
            }`} data-testid="tab-chat">
            <MessageCircle className="w-3 h-3 inline mr-1" /> DISCUSSION
          </button>
          <button
            onClick={() => setView('reflect')}
            className={`flex-1 py-2.5 text-xs font-mono tracking-wider transition-colors ${
              view === 'reflect' ? 'text-amber-300 border-b-2 border-amber-500' : 'text-amber-600/50 hover:text-amber-500'
            }`} data-testid="tab-reflect">
            <PenLine className="w-3 h-3 inline mr-1" /> REFLECTION
          </button>
        </div>

        {/* Chat View */}
        {view === 'chat' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ minHeight: '300px', maxHeight: '50vh' }}>
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-amber-500/60 text-sm font-serif mb-2">Begin your study of</p>
                  <p className="text-amber-300 text-base font-serif font-semibold">{piece?.title || 'this passage'}</p>
                  <p className="text-amber-600/50 text-xs font-mono mt-3">Ask a question or share what you know about this scripture</p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm font-serif leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-amber-900/30 text-amber-200 rounded-br-md'
                      : 'bg-[#0d0a05] border border-amber-900/15 text-amber-300/90 rounded-bl-md'
                  }`} data-testid={`chat-msg-${i}`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl bg-[#0d0a05] border border-amber-900/15 rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-600/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-amber-600/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-amber-600/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat input */}
            <div className="px-4 py-3 border-t border-amber-900/20">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Share your thoughts on this passage..."
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#0d0a05] border border-amber-900/20 text-amber-200 text-sm font-serif placeholder:text-amber-800/40 focus:outline-none focus:border-amber-700/40"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  className="p-2.5 rounded-xl transition-colors disabled:opacity-30"
                  style={{ background: input.trim() ? 'linear-gradient(135deg, #92400e, #d97706)' : 'transparent' }}
                  data-testid="chat-send">
                  <Send className="w-4 h-4 text-amber-100" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Reflection View */}
        {view === 'reflect' && (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" style={{ minHeight: '300px' }}>
            <p className="text-amber-400/80 text-sm font-serif leading-relaxed">
              In your own words, explain what {piece?.scripture_ref || 'this passage'} teaches.
              What did you discover about the grammar and meaning?
            </p>
            <textarea
              value={reflection}
              onChange={e => setReflection(e.target.value)}
              rows={6}
              placeholder="Write your reflection here..."
              className="w-full px-4 py-3 rounded-xl bg-[#0d0a05] border border-amber-900/20 text-amber-200 text-sm font-serif leading-relaxed placeholder:text-amber-800/40 focus:outline-none focus:border-amber-700/40 resize-y"
              data-testid="reflection-input"
            />

            {reflectionFeedback && (
              <div className={`p-4 rounded-xl border ${
                reflectionFeedback.comprehension_met
                  ? 'bg-emerald-900/20 border-emerald-700/30'
                  : 'bg-amber-900/10 border-amber-700/20'
              }`} data-testid="reflection-feedback">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-mono ${reflectionFeedback.comprehension_met ? 'text-emerald-400' : 'text-amber-500'}`}>
                    Score: {reflectionFeedback.score}/10
                  </span>
                  {reflectionFeedback.comprehension_met && (
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                  )}
                </div>
                <p className="text-sm font-serif text-amber-300/90 leading-relaxed">{reflectionFeedback.feedback}</p>
                {reflectionFeedback.growth_note && (
                  <p className="text-xs text-amber-500/70 mt-2 font-serif italic">{reflectionFeedback.growth_note}</p>
                )}
              </div>
            )}

            <button
              onClick={submitReflection}
              disabled={!reflection.trim() || submittingReflection}
              className="w-full py-3 rounded-xl text-sm font-serif transition-all disabled:opacity-30"
              style={{ background: 'linear-gradient(135deg, #92400e, #d97706)', color: '#fef3c7' }}
              data-testid="submit-reflection">
              {submittingReflection ? 'Evaluating...' : 'Submit Reflection'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact progress badge for puzzle pieces
export function ModuleProgressBadge({ moduleKey, progress, unlocked }) {
  const status = progress?.[moduleKey]?.status;
  const isUnlocked = unlocked?.includes(moduleKey);

  if (status === 'completed') {
    return (
      <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 shadow-lg shadow-emerald-900/50" data-testid={`badge-complete-${moduleKey}`}>
        <CheckCircle className="w-3 h-3 text-white" />
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span className="absolute top-1 right-1 w-5 h-5 rounded-full border-2 border-amber-500 bg-amber-900/50 animate-pulse" data-testid={`badge-progress-${moduleKey}`} />
    );
  }
  if (!isUnlocked) {
    return (
      <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 border border-slate-600" data-testid={`badge-locked-${moduleKey}`}>
        <Lock className="w-2.5 h-2.5 text-slate-500" />
      </span>
    );
  }
  return null;
}

export default StudyCompanion;
